import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getActiveEvent, getVelgoerendeFormaal } from '../lib/cms';

export default function GodtFormaal() {
  const [eventData, setEventData] = useState(null);
  const [formaalList, setFormaalList] = useState([]);

  useEffect(() => {
    getActiveEvent().then(setEventData);
    getVelgoerendeFormaal().then(setFormaalList);
  }, []);

  const billetlink = eventData?.billetlink || "https://billet.eventbilletten.dk/seller/foreningen-rbike-zd1k";

  const historiskeResultater = [
    { aar: "2025", resultat: "22.120 DKK", arbejdslon: "30.000,00 kr.", donation: "25.000,00 kr.", modtager: "Team Rynkeby, Børnelungefonden og Børnecancerfonden" },
    { aar: "2024", resultat: "0,00 kr.", arbejdslon: "40.000,00 kr.", donation: "30.000,00 kr.", modtager: "Team Rynkeby, Børnelungefonden og Børnecancerfonden" },
    { aar: "2023", resultat: "0,00 kr.", arbejdslon: "45.000,00 kr.", donation: "0 DKK", modtager: "Team Rynkeby" },
    { aar: "2022", resultat: "0,00 kr.", arbejdslon: "0,00 kr.", donation: "0 DKK", modtager: "Team Rynkeby" },
    { aar: "2021", resultat: "0,00 kr.", arbejdslon: "0,00 kr.", donation: "0 DKK", modtager: "Team Rynkeby" },
    { aar: "2020", resultat: "0,00 kr.", arbejdslon: "0,00 kr.", donation: "10.000,00 kr.", modtager: "Velgørende donation" },
  ];

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Godt formål - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Via Team Rynkeby donerer ØrBike minimum 70% af overskuddet til børn med kritisk sygdom (Børnecancerfonden og Børnelungefonden)."
        />
        <meta property="og:title" content="Godt formål - Foreningen ØrBike" />
        <meta property="og:description" content="Se hvordan ØrBike donerer til børn med kritisk sygdom og se vores historiske donationsoversigt." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#1c1224] via-[#120f1c] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            En god dag med pay-off til velgørenhed
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            For børn med kritisk sygdom
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            En fantastisk dag på cyklen med hjertet det rette sted
          </p>
        </div>
      </section>

      {/* Hovedbudskab & Mission */}
      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 space-y-5">
            <h2 className="text-2xl sm:text-4xl font-black text-white font-display uppercase tracking-tight">
              Minimum 70% af overskuddet doneres
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              Via Team Rynkeby donerer ØrBike minimum 70% af overskuddet fra 2025 og frem til børn med kritisk sygdom. Når du deltager i ØrBike, er du derfor med til at skabe en uforglemmelig dag på cyklen – og samtidig gøre en forskel for børn og familier, der kæmper med alvorlig sygdom.
            </p>
            <p className="text-slate-300 text-base leading-relaxed">
              ØrBike har gennem mange år haft et tæt samarbejde med organisationer der samler penge ind til børn med kritisk sygdom, hvor frivillige har bidraget med stor arbejdskraft til afviklingen af eventet. Som tak for denne indsats har ØrBike hvert år doneret et beløb til disse foreninger eller velgørende fonde i deres navn.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Fra og med <strong>2024</strong> er Foreningen ØrBike ene arrangør af ØrBike og vil hvert år indgå partnerskab med velgørende foreninger, klubber og fonde om at stille frivillig arbejdskraft til rådighed og til gengæld går der en donation til en af dem valgt sag.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Fra og med 2024 har vi valgt at fordele vores donationer ligeligt mellem <strong>Børnelungefonden</strong> og <strong>Børnecancerfonden</strong>, så vi kan støtte endnu flere børn med kritisk sygdom og deres familier.
            </p>
          </div>

          <div className="md:col-span-5 space-y-4">
            <div className="rounded-2xl border border-pink-500/30 overflow-hidden shadow-[0_0_30px_rgba(255,42,141,0.2)]">
              <img
                src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-6557.jpg"
                alt="ØrBike Støtter Børn med kritisk sygdom"
                className="w-full h-72 object-cover"
              />
            </div>
            <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 text-center">
              <span className="text-xs text-slate-400 uppercase font-mono">Samarbejdspartner</span>
              <div className="text-lg font-bold text-white font-display mt-1">Team Rynkeby Ringe</div>
              <div className="text-xs text-pink-400 mt-0.5">Børnecancerfonden & Børnelungefonden</div>
            </div>
          </div>
        </div>

        {/* Historisk overblik over økonomiske resultater & donationer */}
        <div>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
              Gennemsigtighed & Dokumentation
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase tracking-tight mt-1">
              Overblik over økonomiske resultater
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Nedenfor kan du se, hvordan ØrBike gennem årene har doneret, og med en ny omkostningsstruktur fra 2025 og frem håber vi at have fremtidssikret eventet, således at bidraget bliver stigende.
            </p>
          </div>

          {/* Desktop & Tablet Tabel */}
          <div className="hidden sm:block overflow-hidden rounded-2xl border border-pink-500/30 bg-[#12141f] shadow-lg">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900/90 border-b border-pink-500/20 text-xs font-mono uppercase text-pink-400">
                <tr>
                  <th className="py-4 px-6">Regnskabsår</th>
                  <th className="py-4 px-6">Årets resultat efter donation mv.</th>
                  <th className="py-4 px-6">Arbejdsløn foreninger</th>
                  <th className="py-4 px-6">Donation</th>
                  <th className="py-4 px-6">Modtager / Formål</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {historiskeResultater.map((row, i) => (
                  <tr key={i} className="hover:bg-pink-500/5 transition-colors">
                    <td className="py-4 px-6 font-bold text-white font-display text-lg">{row.aar}</td>
                    <td className="py-4 px-6 font-mono text-slate-300">{row.resultat}</td>
                    <td className="py-4 px-6 font-mono text-slate-300">{row.arbejdslon}</td>
                    <td className="py-4 px-6 font-mono font-bold text-pink-400 text-base">{row.donation}</td>
                    <td className="py-4 px-6 text-xs text-slate-300">{row.modtager}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobil Kort Visning */}
          <div className="sm:hidden space-y-3">
            {historiskeResultater.map((row, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#12141f] border border-slate-800 space-y-2">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <span className="font-bold text-white font-display text-xl">{row.aar}</span>
                  <span className="font-mono font-bold text-pink-400 text-base">{row.donation}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                  <div>
                    <span className="block text-slate-500">Årets resultat efter donation mv.:</span>
                    <span className="text-slate-300">{row.resultat}</span>
                  </div>
                  <div>
                    <span className="block text-slate-500">Arbejdsløn:</span>
                    <span className="text-slate-300">{row.arbejdslon}</span>
                  </div>
                </div>
                <div className="text-[11px] text-slate-400 pt-1">
                  <strong>Formål:</strong> {row.modtager}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-pink-950/40 via-purple-950/30 to-slate-900 border border-pink-500/30 rounded-3xl p-8 sm:p-12">
          <h3 className="text-2xl sm:text-4xl font-black text-white font-display uppercase">
            Vær med til at gøre en forskel
          </h3>
          <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-xl mx-auto">
            Hver eneste billet du køber er med til at skabe uforglemmelige smil og støtte børn med kritisk sygdom.
          </p>
          <div className="mt-6 flex justify-center">
            <a
              href={billetlink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-[0_0_25px_rgba(255,42,141,0.5)] hover:scale-105 transition-transform"
            >
              <i className="fa-solid fa-ticket" aria-hidden="true"></i>
              <span>Køb billet på Eventbilletten</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
